import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  DocIcon,
  SlidersIcon,
  RobotIcon,
  CodeIcon,
} from "../Pages/Auth/onboarding/icons";
import {
  HomeIcon,
  ChatBubbleIcon,
  BarChartIcon,
  PuzzleIcon,
  ChevronDownIcon,
} from "../Pages/Dashboard/icons";
import { RoutesPaths } from "../Router/constant";
import {
  listAssistants,
  setActiveAssistant,
  livingAssistants,
} from "../actions/assistants";
import { hostOf } from "../utils/url";
import SubscriptionCard from "./SubscriptionCard";

const navItems = [
  { label: "Overview", icon: <HomeIcon />, path: RoutesPaths.DASHBOARD },
  {
    label: "Conversations",
    icon: <ChatBubbleIcon />,
    path: RoutesPaths.CONVERSATIONS,
  },
  // { label: "Leads", icon: <UsersIcon /> },
  // { label: "Knowledge", icon: <BookIcon /> },
  // { label: "Analytics", icon: <BarChartIcon />, path: RoutesPaths.ANALYTICS },
  // { label: "Assistant", icon: <RobotIcon />, path: RoutesPaths.ASSISTANT },
  {
    label: "Customization",
    icon: <SlidersIcon />,
    path: RoutesPaths.CUSTOMIZATION,
  },
  {
    label: "Install & Settings",
    icon: <CodeIcon />,
    path: RoutesPaths.INSTALL_SETTINGS,
  },
  // { label: "Team", icon: <UsersIcon /> },
  {
    label: "Integrations",
    icon: <PuzzleIcon />,
    path: RoutesPaths.INTEGRATIONS,
  },
  // { label: "Billing", icon: <DocIcon />, path: RoutesPaths.BILLING },
  { label: "Training Assistant", icon: <RobotIcon />, path: RoutesPaths.TRAINING_BOT },
];

const initialLetter = (name) =>
  (name || "").trim().charAt(0).toUpperCase() || "?";

/**
 * Assistant switcher. The chosen id is written to localStorage so the rest of
 * the app can read which assistant is in context, and so the pick survives a
 * refresh. With nothing stored the first assistant is selected.
 */
function AssistantSwitcher() {
  const dispatch = useDispatch();
  // Minus any the API has already answered 404 for. A list that still carries
  // a removed assistant would otherwise be enough to re-select it, and the
  // pages reading the active one would 404 over and over.
  const stored = useSelector((state) => state.assistants.list);
  // Memoised: filtering builds a new array, and this is a dependency of the
  // effects below - a fresh identity on every render would re-run them all.
  const assistants = useMemo(() => livingAssistants(stored), [stored]);
  const loading = useSelector((state) => state.assistants.loading);
  // Whether GET /assistants itself is in flight. The shared flag is no use
  // here: the dashboard's own requests lower it while the list is still on
  // its way, and the switcher would then act on the copy it is replacing.
  const listLoading = useSelector((state) => state.assistants.listLoading);
  const activeId = useSelector((state) => state.assistants.activeId);
  // The assistant onboarding just created lands in the store before the
  // refreshed list does, so the switcher can name the one it is pointed at
  // rather than reading "Select assistant" for a beat after going live.
  const created = useSelector((state) => state.assistants.current);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  // The list is shared with the Assistant page, so only fetch it when nothing
  // has loaded it yet.
  useEffect(() => {
    // Not while one is already in flight: going live starts this same
    // request a moment before the dashboard - and this sidebar - mounts.
    if (!assistants.length && !listLoading) dispatch(listAssistants());
    // Deliberately mount-only: refetching whenever the list empties would loop
    // on an account that genuinely has no assistants.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  // Falls back to the first assistant when nothing is stored, or when the
  // stored id points at one that has since been deleted.
  useEffect(() => {
    // A list still being refetched is not evidence that the id is gone: an
    // assistant created a moment ago is missing from the copy in the store
    // until the new one lands, and reassigning here would send the dashboard
    // back to the previous assistant.
    if (listLoading) return;
    // The one just created is not in any list yet, however long it has been
    // since the last fetch. It is the assistant the user is looking at.
    if (activeId && created?.id === activeId) return;
    if (!assistants.length) return;
    if (assistants.some((item) => item.id === activeId)) return;

    dispatch(setActiveAssistant(assistants[0].id));
  }, [assistants, activeId, created, listLoading, dispatch]);

  // A dropdown that stays open after clicking away feels broken.
  useEffect(() => {
    if (!open) return undefined;

    const onPointerDown = (event) => {
      if (!wrapRef.current?.contains(event.target)) setOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const navigate = useNavigate();

  const active =
    assistants.find((item) => item.id === activeId) ||
    (activeId && created?.id === activeId ? created : null);

  const select = (assistant) => {
    dispatch(setActiveAssistant(assistant.id));
    setOpen(false);
  };

  // The create flow is the assistant steps, on a route of their own.
  const addAssistant = () => {
    setOpen(false);
    navigate(RoutesPaths.CREATE_ASSISTANT);
  };

  const placeholder = loading ? "Loading…" : "No assistants";
  const label =
    active?.name || (assistants.length ? "Select assistant" : placeholder);
  const sub = active ? hostOf(active.sourceUrl) || "Assistant" : "—";

  return (
    <div className="dash-sidebar__switcher" ref={wrapRef}>
      <button
        type="button"
        className="dash-sidebar__workspace dash-sidebar__workspace--top"
        onClick={() => setOpen((value) => !value)}
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={loading && !assistants.length}
      >
        <span className="dash-sidebar__workspace-avatar">
          {initialLetter(active?.name)}
        </span>
        <span className="dash-sidebar__workspace-text">
          <strong>{label}</strong>
          <span>{sub}</span>
        </span>
        <ChevronDownIcon />
      </button>

      {open && (
        <ul className="dash-sidebar__switcher-menu" role="listbox">
          {assistants.map((assistant) => {
            const isActive = assistant.id === activeId;

            return (
              <li key={assistant.id}>
                <button
                  type="button"
                  role="option"
                  aria-selected={isActive}
                  className={`dash-sidebar__switcher-option${isActive ? " is-active" : ""}`}
                  onClick={() => select(assistant)}
                >
                  <span className="dash-sidebar__workspace-avatar">
                    {initialLetter(assistant.name)}
                  </span>
                  <span className="dash-sidebar__workspace-text">
                    <strong>{assistant.name || "Untitled assistant"}</strong>
                    <span>{hostOf(assistant.sourceUrl)}</span>
                  </span>
                  {isActive && (
                    <span className="dash-sidebar__switcher-check">✓</span>
                  )}
                </button>
              </li>
            );
          })}

          <li className="dash-sidebar__switcher-add-row">
            <button
              type="button"
              className="dash-sidebar__switcher-add"
              onClick={addAssistant}
            >
              <span className="dash-sidebar__switcher-add-icon" aria-hidden="true">
                +
              </span>
              Add new assistant
            </button>
          </li>
        </ul>
      )}
    </div>
  );
}

export default function Sidebar({ promo }) {
  const location = useLocation();
  // Rides along on the GET /auth/me record that verifyToken() already stores,
  // so the plan card costs no extra request.
  const subscription = useSelector((state) => state.auth.user?.subscription);
  // Pairs with the plan's maxAssistants allowance.
  const assistantCount = useSelector((state) => state.assistants.list.length);

  return (
    <aside className="dash-sidebar">
      <Link to={RoutesPaths.DASHBOARD} className="dash-sidebar__logo">
        <img src="/assets/logo.svg" alt="" className="img-fluid" />
      </Link>

      <AssistantSwitcher />

      <nav className="dash-sidebar__nav">
        {navItems.map((item) => {
          // Sub-paths count too, so /assistant/:id keeps Assistant highlighted.
          const isActive =
            item.path &&
            (location.pathname === item.path ||
              location.pathname.startsWith(`${item.path}/`));
          const className = `dash-sidebar__link${isActive ? " is-active" : ""}`;

          if (item.path) {
            return (
              <Link to={item.path} key={item.label} className={className}>
                {item.icon}
                {item.label}
              </Link>
            );
          }

          return (
            <button type="button" key={item.label} className={className}>
              {item.icon}
              {item.label}
            </button>
          );
        })}
      </nav>

      {promo || (
        <SubscriptionCard
          subscription={subscription}
          assistantCount={assistantCount}
        />
      )}

      {/* <button type="button" className="dash-sidebar__account">
        <span className="dash-sidebar__account-avatar">AM</span>
        <span className="dash-sidebar__ account-text">
          <strong>Ariun Mehta</strong>
          <span>admin@gopointr.com</span>
        </span>
        <ChevronDownIcon />
      </button> */}
    </aside>
  );
}
